import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Home as HomeIcon, Droplets, Apple, HeartPulse, GraduationCap, ShieldCheck, Info, Gamepad2, Bath, ArrowRight, MapPin, Users, Heart } from "lucide-react";
import { PHOTO_HOME } from "@/lib/assets";

const dimensions = [
  { icon: HomeIcon, label: "Housing" },
  { icon: Bath, label: "Sanitation" },
  { icon: Droplets, label: "Clean water" },
  { icon: Apple, label: "Nutrition" },
  { icon: HeartPulse, label: "Health" },
  { icon: GraduationCap, label: "Education" },
  { icon: ShieldCheck, label: "Child protection" },
  { icon: Info, label: "Access to information & participation" },
  { icon: Gamepad2, label: "Recreation & leisure" },
];

const regions = [
  { name: "Somali Region", pct: "88.2%", note: "The highest rate identified in the study." },
  { name: "Afar Region", pct: "87.9%", note: "Communities continue to face poverty, drought, food insecurity, water challenges, and loss of livelihoods." },
  { name: "Southern Ethiopia", pct: "86.4%", note: "High levels of child deprivation affect many communities across southern Ethiopia." },
  { name: "Tigray Region", pct: "86.1%", note: "Children and families continue to experience the effects of displacement, conflict, interrupted education, poverty, and limited access to essential services." },
];

const targetAreas = [
  { name: "Somali Region", focus: "Child poverty • Water access • Nutrition • Education • Family support" },
  { name: "Afar Region", focus: "Drought-affected families • Food security • Girls' education • Water • Family assistance" },
  { name: "Tigray Region", focus: "Displaced children • Trauma recovery • Education • Family reunification and support • Basic needs" },
  { name: "Amhara Region", focus: "Conflict-affected children • Education • Child protection • Family assistance" },
  { name: "Oromia Region", focus: "Rural poverty • Education • Child protection • Food security • Family support" },
  { name: "Southern Ethiopia", focus: "Housing • Sanitation • Nutrition • Education • Rural family assistance" },
];

const factors = [
  "Child poverty", "Children without safe parental care", "Girls at increased risk",
  "School dropout and lack of educational access", "Food insecurity and malnutrition",
  "Unsafe or inadequate housing", "Access to clean water and sanitation", "Displacement",
  "Child protection concerns", "Access to healthcare", "Availability of existing community resources",
  "Opportunities to keep children safely with their families",
];

const flow = ["Region", "Zone", "Woreda", "Town or Village", "Children & Families"];

export default function TheNeedPage() {
  return (
    <div data-testid="the-need-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#1F4D2E] text-white">
        <div className="absolute inset-0">
          <img src={PHOTO_HOME} alt="Children in Ethiopia" loading="lazy" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1F4D2E] via-[#1F4D2E]/85 to-[#1F4D2E]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E0A526] mb-4">Understanding the Need</p>
          <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight leading-[0.95] max-w-3xl">Child Poverty in Ethiopia</h1>
          <p className="text-lg text-[#E7EDE2]/90 mt-6 max-w-2xl leading-relaxed">Mercy's Village Ethiopia is committed to using research, local knowledge, and community partnerships to identify where children and families are most vulnerable.</p>
        </div>
      </section>

      {/* UNICEF STUDY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-4">UNICEF 2024 Study</p>
            <div className="flex items-end gap-3 mb-4">
              <span className="font-display text-7xl font-bold text-[#C1272D] leading-none">80.4%</span>
            </div>
            <p className="text-[#4F6B54] leading-relaxed text-lg">of children in Ethiopia experience deprivation in <span className="font-semibold text-[#1F4D2E]">five or more areas</span> of basic well-being, according to UNICEF's 2024 <em>Multidimensional Child Poverty in Ethiopia</em> study.</p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="bg-[#FBF8F0] border border-[#1F4D2E]/10 rounded-2xl p-6">
                <span className="font-display text-4xl font-bold text-[#1F4D2E]">84.8%</span>
                <p className="text-sm text-[#4F6B54] mt-2">of <strong>rural</strong> children are multidimensionally deprived</p>
              </div>
              <div className="bg-[#FBF8F0] border border-[#1F4D2E]/10 rounded-2xl p-6">
                <span className="font-display text-4xl font-bold text-[#1F4D2E]">55.2%</span>
                <p className="text-sm text-[#4F6B54] mt-2">of <strong>urban</strong> children are multidimensionally deprived</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <h2 className="font-display text-3xl font-semibold text-[#1F4D2E] mb-6">Nine dimensions of child well-being</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {dimensions.map((d, i) => (
                <motion.div key={d.label} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.04 }}
                  className="flex items-center gap-3 bg-white border border-[#1F4D2E]/10 rounded-xl px-4 py-3.5">
                  <span className="w-9 h-9 rounded-full bg-[#1F4D2E]/8 text-[#1F4D2E] flex items-center justify-center shrink-0"><d.icon size={18} /></span>
                  <span className="text-[#1F4D2E] text-sm font-medium">{d.label}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HIGHEST-NEED REGIONS */}
      <section className="bg-[#1F4D2E] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E0A526] mb-3">Where the need is greatest</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mb-4">Some of Ethiopia's highest-need regions</h2>
          <p className="text-[#E7EDE2]/80 max-w-2xl mb-12">The study identified several regions with particularly high levels of multidimensional child deprivation.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {regions.map((r, i) => (
              <motion.div key={r.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-7 backdrop-blur">
                <div className="flex items-baseline justify-between gap-3 mb-3">
                  <h3 className="font-display text-2xl font-semibold">{r.name}</h3>
                  <span className="font-display text-3xl font-bold text-[#E0A526]">{r.pct}</span>
                </div>
                <p className="text-sm text-[#E7EDE2]/75 leading-relaxed">{r.note}</p>
              </motion.div>
            ))}
          </div>
          <p className="text-xs text-[#E7EDE2]/50 mt-8 max-w-3xl leading-relaxed">The UNICEF study used the former Southern Nations, Nationalities and Peoples Region (SNNPR) statistical grouping because the available survey framework did not yet permit full separation of Ethiopia's newly formed southern regions.</p>
        </div>
      </section>

      {/* FUTURE TARGET AREAS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Our future target areas</p>
        <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F4D2E] mb-4">Going where children need support most</h2>
        <p className="text-[#4F6B54] max-w-2xl mb-12 leading-relaxed">Mercy's Village Ethiopia plans to expand into communities where vulnerable children and struggling families face some of the greatest barriers to safety, education, nutrition, shelter, healthcare, and opportunity.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {targetAreas.map((a, i) => (
            <motion.div key={a.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }}
              className="bg-[#FBF8F0] border border-[#1F4D2E]/10 rounded-2xl p-7 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] transition-all duration-300">
              <div className="flex items-center gap-2.5 mb-4">
                <span className="w-10 h-10 rounded-full bg-[#C1272D]/10 text-[#C1272D] flex items-center justify-center"><MapPin size={19} /></span>
                <h3 className="font-display text-2xl font-semibold text-[#1F4D2E]">{a.name}</h3>
              </div>
              <p className="text-xs tracking-[0.12em] uppercase font-bold text-[#4F6B54]/60 mb-2">Future focus</p>
              <p className="text-[#4F6B54] text-sm leading-relaxed">{a.focus}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW WE CHOOSE */}
      <section className="bg-[#ECECE8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Our approach</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F4D2E] mb-4">How we will choose communities</h2>
          <p className="text-[#4F6B54] max-w-2xl mb-10 leading-relaxed">These regions represent areas we intend to study and potentially serve in the future. They are not yet Mercy's Village operating locations. Before entering a community, we intend to assess needs at the local level:</p>

          <div className="flex flex-wrap items-center gap-3 mb-14">
            {flow.map((step, i) => (
              <React.Fragment key={step}>
                <span className="bg-white border border-[#1F4D2E]/10 rounded-full px-5 py-2.5 text-sm font-semibold text-[#1F4D2E]">{step}</span>
                {i < flow.length - 1 && <ArrowRight size={18} className="text-[#C1272D]" />}
              </React.Fragment>
            ))}
          </div>

          <h3 className="font-display text-2xl font-semibold text-[#1F4D2E] mb-6">Our decisions will consider:</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
            {factors.map((f) => (
              <div key={f} className="flex items-start gap-2.5">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#E0A526] shrink-0" />
                <span className="text-[#4F6B54] text-sm">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMITMENT */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-24 text-center">
        <span className="w-14 h-14 rounded-full bg-[#C1272D]/10 text-[#C1272D] flex items-center justify-center mx-auto mb-6"><Users size={26} /></span>
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-4">Our commitment</p>
        <p className="font-display text-3xl sm:text-4xl font-semibold text-[#1F4D2E] leading-snug mb-8">Poverty alone should never be a reason to separate a child from a loving and safe family.</p>
        <p className="text-[#4F6B54] leading-relaxed text-lg mb-4">Whenever possible, Mercy's Village Ethiopia will work to strengthen families and help children remain safely with parents, grandparents, relatives, or other appropriate caregivers.</p>
        <p className="text-[#4F6B54] leading-relaxed">Residential care will be considered for children who cannot safely remain in their existing family environment and who require a higher level of protection and support.</p>
      </section>

      {/* LONG-TERM VISION */}
      <section className="bg-[#1F4D2E] text-white relative overflow-hidden">
        <span className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-[#E0A526]/10" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 py-24 text-center">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E0A526] mb-6">Our long-term vision</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-8">Protect Children. Preserve Families.<br />Educate. Empower. Build Stronger Communities.</h2>
          <p className="text-[#E7EDE2]/85 leading-relaxed text-lg max-w-2xl mx-auto">Our goal is not simply to respond to poverty. Our goal is to help create pathways that allow Ethiopian children, families, and communities to build safer and more hopeful futures.</p>
          <div className="flex flex-wrap gap-4 justify-center mt-10">
            <Link to="/projects" data-testid="need-sponsor-btn" className="bg-[#C1272D] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#9E1F24] transition-colors inline-flex items-center gap-2"><Heart size={18} fill="white" /> Sponsor a Room</Link>
            <Link to="/about" data-testid="need-about-btn" className="border border-white/50 rounded-full px-8 py-4 font-semibold hover:bg-white hover:text-[#1F4D2E] transition-colors inline-flex items-center gap-2">Our mission <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>

      <p className="text-center text-xs text-[#4F6B54]/70 py-8 px-6">Research source: UNICEF Ethiopia, <em>Multidimensional Child Poverty in Ethiopia</em>, 2024.</p>
    </div>
  );
}
